import React, { useState } from 'react'
import { Link, useForm, usePage } from '@inertiajs/inertia-react'
import axios from 'axios'
import AlertDismissible from '../../../Shared/Components/AlertDismissible'
import Breadcrumbs from '../../../Shared/Components/Breadcrumbs'
import FormValidationSummary, { mergeValidationErrors, validationMessage } from '../../../Shared/Components/FormValidationSummary'
import Layout from '../../../Shared/Layout/LayoutAdmin'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import Modal from 'react-bootstrap/Modal'
import { SIA_NAVY } from '../../../constants/siaTheme'

function boolToForm(val) {
    return val === true || val === 1 || val === '1'
}

function nowTimeHHmm() {
    const d = new Date()
    const h = String(d.getHours()).padStart(2, '0')
    const m = String(d.getMinutes()).padStart(2, '0')
    return `${h}:${m}`
}

function formatTimeAmPm(val) {
    if (!val) return ''
    const parts = String(val).slice(0, 8).split(':')
    const hour24 = Number.parseInt(parts[0], 10)
    const minutes = parts[1] ?? '00'
    if (Number.isNaN(hour24)) return String(val)

    const suffix = hour24 >= 12 ? 'PM' : 'AM'
    let hour12 = hour24 % 12
    if (hour12 === 0) hour12 = 12

    return `${String(hour12).padStart(2, '0')}:${minutes} ${suffix}`
}

const Reqd = () => <span className="text-danger">*</span>

function FieldLabel({ children, required = true }) {
    return (
        <Form.Label className="fw-bold text-gray-800">
            {children}:{required ? ' ' : ''}
            {required && <Reqd />}
        </Form.Label>
    )
}

function YesNoGroup({ label, name, value, onChange, error }) {
    const yes = boolToForm(value)
    return (
        <div className="mb-4">
            <Form.Label className="fw-bold d-block text-gray-800">{label}</Form.Label>
            <div className="d-flex gap-4">
                <Form.Check type="radio" id={`${name}-si`} name={name} label="SI" checked={yes} onChange={() => onChange(true)} />
                <Form.Check type="radio" id={`${name}-no`} name={name} label="NO" checked={!yes} onChange={() => onChange(false)} />
            </div>
            {error && <div className="text-danger fs-8 mt-1">{error}</div>}
        </div>
    )
}

const CustomerServiceForm = ({ entry, mode, canEdit, catalogs, advisers }) => {
    const { flash, errors: pageErrors } = usePage().props
    const manage = mode === 'manage'
    const isEdit = Boolean(entry)
    const [saving, setSaving] = useState(false)
    const [policyLoading, setPolicyLoading] = useState(false)
    const [policyError, setPolicyError] = useState('')
    const [cancelledModal, setCancelledModal] = useState(false)

    const form = useForm({
        numero_poliza: entry?.numero_poliza || '',
        hora_inicio: '',
        nombre: entry?.nombre || '',
        apellido_paterno: entry?.apellido_paterno || '',
        apellido_materno: entry?.apellido_materno || '',
        sia_visit_type_id: entry?.sia_visit_type_id ? String(entry.sia_visit_type_id) : '',
        telefono: entry?.telefono || '',
        email: entry?.email || '',
        calle_numero: entry?.calle_numero || '',
        colonia: entry?.colonia || '',
        ciudad_municipio: entry?.ciudad_municipio || '',
        estado: entry?.estado || '',
        codigo_postal: entry?.codigo_postal || '',
        cantidad: entry?.cantidad ?? '0',
        observaciones: entry?.observaciones || '',
        nombre_escuela: entry?.nombre_escuela || '',
        direccion_escuela: entry?.direccion_escuela || '',
        otro_seguro_talon_pago: boolToForm(entry?.otro_seguro_talon_pago),
        sia_other_insurance_type_id: entry?.sia_other_insurance_type_id ? String(entry.sia_other_insurance_type_id) : '',
        monto_otro_seguro: entry?.monto_otro_seguro ?? '0',
        clave_cct: entry?.clave_cct || '',
        rfc: entry?.rfc || '',
        adviser_id: entry?.adviser_id ? String(entry.adviser_id) : '',
    })

    const allErrors = mergeValidationErrors(form.errors, pageErrors)
    const fieldError = (key) => validationMessage(allErrors, key)
    const invalidClass = (key) => (fieldError(key) ? 'is-invalid' : '')
    const FieldFeedback = ({ name }) =>
        fieldError(name) ? <div className="invalid-feedback d-block">{fieldError(name)}</div> : null

    const indexRoute = 'customer-service.index'
    const pathPrefix = 'admin/atencion-cliente/registros'

    const lookupPolicy = async () => {
        const num = String(form.data.numero_poliza || '').trim()
        if (!num) {
            setPolicyError('Ingrese el número de póliza.')
            return
        }

        setPolicyLoading(true)
        setPolicyError('')

        try {
            const { data } = await axios.get(route('customer-service.lookup-policy'), {
                params: { policy_number: num },
            })

            if (!data.found) {
                if (data.cancelled) {
                    setCancelledModal(true)
                    setPolicyError('')
                } else {
                    setPolicyError(data.message || 'Póliza no encontrada.')
                }
                return
            }

            const startTime = nowTimeHHmm()
            const prefill = data.prefill || {}

            form.setData((current) => ({
                ...current,
                numero_poliza: num,
                hora_inicio: isEdit ? current.hora_inicio : startTime,
                nombre: prefill.nombre ?? current.nombre,
                apellido_paterno: prefill.apellido_paterno ?? current.apellido_paterno,
                apellido_materno: prefill.apellido_materno ?? current.apellido_materno,
                telefono: prefill.telefono ?? current.telefono,
                email: prefill.email ?? current.email,
                rfc: prefill.rfc || current.rfc,
            }))
        } catch {
            setPolicyError('No se pudo consultar la póliza.')
        } finally {
            setPolicyLoading(false)
        }
    }

    const buildPayload = () => {
        const payload = {
            ...form.data,
            otro_seguro_talon_pago: form.data.otro_seguro_talon_pago ? 1 : 0,
            cantidad: Number(form.data.cantidad) || 0,
            monto_otro_seguro: Number(form.data.monto_otro_seguro) || 0,
            adviser_id: form.data.adviser_id || null,
        }

        if (isEdit) {
            delete payload.hora_inicio
        } else if (!payload.hora_inicio) {
            payload.hora_inicio = nowTimeHHmm()
        }

        return payload
    }

    const submit = (e) => {
        e.preventDefault()
        setSaving(true)
        const opts = { preserveScroll: true, onFinish: () => setSaving(false) }

        form.transform(() => buildPayload())

        if (manage) {
            if (isEdit) {
                form.put(route('customer-service.update', entry.id), opts)
            } else {
                form.post(route('customer-service.store'), opts)
            }
        } else if (isEdit) {
            form.put(route('customer-service.update', entry.id), opts)
        } else {
            form.post(route('customer-service.store'), opts)
        }
    }

    const catalogSelect = (label, field, options, placeholder) => (
        <div className="mb-4">
            <FieldLabel>{label}</FieldLabel>
            <Form.Select
                value={form.data[field]}
                onChange={(e) => form.setData(field, e.target.value)}
                className={invalidClass(field)}
                disabled={!canEdit}
            >
                <option value="">{placeholder}</option>
                {(options || []).map((o) => (
                    <option key={o.id} value={o.id}>
                        {o.name}
                    </option>
                ))}
            </Form.Select>
            <FieldFeedback name={field} />
        </div>
    )

    const textField = (label, field, placeholder, type = 'text', rows = null) => (
        <div className="mb-4">
            <FieldLabel>{label}</FieldLabel>
            <Form.Control
                type={type}
                as={rows ? 'textarea' : undefined}
                rows={rows || undefined}
                placeholder={placeholder}
                value={form.data[field]}
                onChange={(e) => form.setData(field, e.target.value)}
                className={invalidClass(field)}
                disabled={!canEdit}
            />
            <FieldFeedback name={field} />
        </div>
    )

    return (
        <Row>
            {flash?.success && <AlertDismissible message={flash.success} type="success" />}
            <FormValidationSummary errors={allErrors} />
            <span className="pb-7 pt-7">
                <Breadcrumbs currentPath={pathPrefix + (isEdit ? `/${entry.id}/edit` : '/create')} />
            </span>

            <div
                className="rounded-2 mb-6 py-4 px-4 text-center text-white text-uppercase fw-bold fs-5"
                style={{ backgroundColor: SIA_NAVY, letterSpacing: '0.04em' }}
            >
                Formulario de registro
            </div>

            <Card className="card-flush shadow-sm mb-10">
                <Card.Body className="py-8 px-6 px-lg-10">
                    <Form onSubmit={submit}>
                        <div className="mb-8">
                            <FieldLabel>Número de póliza</FieldLabel>
                            <div className="d-flex flex-wrap gap-2 align-items-start">
                                <div className="flex-grow-1" style={{ minWidth: '220px' }}>
                                    <Form.Control
                                        type="text"
                                        placeholder="INGRESA LA PÓLIZA"
                                        value={form.data.numero_poliza}
                                        onChange={(e) => {
                                            form.setData('numero_poliza', e.target.value)
                                            setPolicyError('')
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault()
                                                if (canEdit) lookupPolicy()
                                            }
                                        }}
                                        className={invalidClass('numero_poliza')}
                                        disabled={!canEdit}
                                    />
                                    <FieldFeedback name="numero_poliza" />
                                </div>
                                {canEdit && (
                                    <Button
                                        type="button"
                                        variant="secondary"
                                        onClick={lookupPolicy}
                                        disabled={policyLoading}
                                    >
                                        {policyLoading ? 'Buscando…' : 'Buscar'}
                                    </Button>
                                )}
                            </div>
                            {policyError && <div className="text-danger fs-8 mt-2">{policyError}</div>}
                            {!isEdit && form.data.hora_inicio && (
                                <div className="text-muted fs-8 mt-2">
                                    Hora de inicio registrada: {formatTimeAmPm(form.data.hora_inicio)}
                                </div>
                            )}
                        </div>

                        <Row className="g-6">
                            <Col lg={6}>
                                {textField('Nombre', 'nombre', 'INGRESA EL NOMBRE')}
                                {textField('Apellido Paterno', 'apellido_paterno', 'INGRESA EL APELLIDO PATERNO')}
                                {textField('Apellido Materno', 'apellido_materno', 'INGRESA EL APELLIDO MATERNO')}
                                {catalogSelect('Tipo de visita', 'sia_visit_type_id', catalogs.tipos_visita, 'Selecciona un tipo de visita')}
                                {textField('Teléfono', 'telefono', 'INGRESA EL TELÉFONO')}
                                {textField('Correo electrónico', 'email', 'INGRESA EL CORREO ELECTRÓNICO', 'email')}
                                {textField('Calle y número', 'calle_numero', 'INGRESA LA CALLE')}
                                {textField('Colonia', 'colonia', 'INGRESA LA COLONIA')}
                                {textField('Ciudad o Municipio', 'ciudad_municipio', 'INGRESA LA CIUDAD')}
                                {textField('Estado', 'estado', 'INGRESA EL ESTADO')}
                                {textField('Código Postal', 'codigo_postal', 'INGRESA EL CÓDIGO POSTAL')}
                            </Col>

                            <Col lg={6}>
                                {textField('Cantidad (en caso de ser dividendos)', 'cantidad', '0', 'number')}
                                {textField('Observaciones', 'observaciones', 'INGRESA LAS OBSERVACIONES', 'text', 3)}
                                {textField('Nombre de la escuela', 'nombre_escuela', 'INGRESA EL NOMBRE DE LA ESCUELA')}
                                {textField('Dirección de la escuela', 'direccion_escuela', 'INGRESA LA DIRECCIÓN DE LA ESCUELA')}

                                <YesNoGroup
                                    label="¿Otro seguro en talón de pago?"
                                    name="otro_seguro"
                                    value={form.data.otro_seguro_talon_pago}
                                    onChange={(v) => form.setData('otro_seguro_talon_pago', v)}
                                    error={fieldError('otro_seguro_talon_pago')}
                                />

                                {catalogSelect(
                                    'Tipo de otro seguro',
                                    'sia_other_insurance_type_id',
                                    catalogs.tipos_otro_seguro,
                                    'Selecciona un tipo de otro seguro',
                                )}
                                {textField('Monto del otro seguro', 'monto_otro_seguro', '0', 'number')}
                                {textField('Clave de centro de trabajo', 'clave_cct', 'INGRESA EL CCT')}
                                {textField('RFC', 'rfc', 'INGRESA EL RFC')}
                            </Col>
                        </Row>

                        <div className="mt-6">
                            <div
                                className="text-center fw-bold text-uppercase py-3 mb-4 rounded-1"
                                style={{ backgroundColor: '#f1f3f5', color: '#5e6278', letterSpacing: '0.06em' }}
                            >
                                Solo si aplica
                            </div>
                            <div className="mb-4">
                                <FieldLabel required={false}>Seleccione el asesor que entrega</FieldLabel>
                                <Form.Select
                                    value={form.data.adviser_id}
                                    onChange={(e) => form.setData('adviser_id', e.target.value)}
                                    className={invalidClass('adviser_id')}
                                    disabled={!canEdit}
                                >
                                    <option value="">Seleccione una opción</option>
                                    {(advisers || []).map((a) => (
                                        <option key={a.id} value={a.id}>
                                            {a.display_name}
                                        </option>
                                    ))}
                                </Form.Select>
                                <FieldFeedback name="adviser_id" />
                            </div>
                        </div>

                        <div className="d-flex flex-column align-items-center gap-4 mt-8">
                            <Button type="submit" variant="primary" size="lg" disabled={saving || form.processing || !canEdit}>
                                <i className="bi bi-save me-2" />
                                {saving ? 'Guardando…' : 'Registrar'}
                            </Button>
                            <Link href={route(indexRoute)} className="btn btn-light btn-lg">
                                <i className="bi bi-arrow-left me-2" />
                                Regresar
                            </Link>
                        </div>
                    </Form>
                </Card.Body>
            </Card>

            <Modal show={cancelledModal} onHide={() => setCancelledModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Póliza cancelada</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    No se aceptan pólizas canceladas.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => setCancelledModal(false)}>
                        Entendido
                    </Button>
                </Modal.Footer>
            </Modal>
        </Row>
    )
}

CustomerServiceForm.layout = (page) => <Layout title="Atención al cliente">{page}</Layout>

export default CustomerServiceForm
